import { useNavigation } from '@app/hooks/useNavigation';
import { useUserValuesData } from '@app/hooks/useUserValuesData';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IMessage, MessageNames } from '../context/MessageContext';
import { NavigationState } from '../context/NavigationContext';

import { useMessageQueue } from '@app/context/MessageQueueContext';
import { useTasks } from '@app/context/TaskContext';
import { hashObject } from '@app/utils/hashObject';
import { UserValue } from '@dieterApi/persistentState/useUserValuesQuery';
import { Tasks } from '@dieterApi/task/useTaskQuery';
import { UserWithMethods, useUserWithCompany } from './useUserWithCompany';
// Define the state for a message
export interface IMessageState {
  isOpen: boolean; // Whether the message is currently open
  wasOpen?: boolean; // Whether the message was open before
  contextHash?: string; // The hash of the context object
}

interface IConditionContext {
  user: UserWithMethods | undefined;
  tasks: Tasks | undefined;
  navigation: NavigationState;
  userValues: UserValue[] | undefined;
  /** Resolved whitelabel config for this session, from token, active company or host. */
  whitelabelConfig: unknown;
  /** True while that resolution is still in flight — conditions should fail closed on it. */
  whitelabelLoading: boolean;
}

export interface MessageDefinition {
  name: MessageNames;
  recurring: boolean;
  condition: (context: IConditionContext) => boolean;
  order?: number;
}

/**
 * Factory function to create a custom hook for managing a message.
 *
 * @param messageName - The name of the message.
 * @param recurring - Whether the message is recurring.
 * @param evaluateCondition - Function to evaluate whether the condition for showing the message is met.
 * @returns A custom hook for managing the message.
 */
export function createUseMessageHook({ name, recurring, condition, order = 99 }: MessageDefinition) {
  // The custom hook
  return function useMessage(): IMessage {
    // Get the navigation state and the function to update it
    const { navigation, setNavigation, hasLoaded } = useNavigation();
    // Get the user state
    const { user, whitelabelConfig, whitelabelLoading } = useUserWithCompany();
    // Get the user values
    const { userValues } = useUserValuesData();
    // Get the tasks state
    const tasks = useTasks();

    const { messageQueue, enqueueMessage, dequeueMessage } = useMessageQueue();

    // Basic state for the message
    const [state, setState] = useState<IMessageState>({
      isOpen: false,
    });

    // Use ref to track if message has been enqueued to prevent redundant enqueues
    const enqueuedRef = useRef(false);

    // Reset message state when user changes (logout/login scenario)
    const userIdRef = useRef(user?.id);
    useEffect(() => {
      if (userIdRef.current !== user?.id) {
        userIdRef.current = user?.id;
        setState({ isOpen: false });
        enqueuedRef.current = false;
        dequeueMessage(name);
      }
    }, [user?.id, name, dequeueMessage]);

    // Memoize callbacks to prevent recreation on every render
    const open = useCallback(() => {
      setState((prev) => ({ ...prev, isOpen: true }));
    }, []);

    const close = useCallback(() => {
      setState((prev) => {
        setNavigation((nav) => {
          if (!nav.dismissedMessages.find((m) => m.contextHash === prev.contextHash && m.name === name))
            nav.dismissedMessages.push({ contextHash: prev.contextHash!, name: name });
        });
        dequeueMessage(name);
        return { ...prev, isOpen: false, wasOpen: true };
      });
    }, [name, setNavigation, dequeueMessage]);

    const setContext = useCallback((context: any) => {
      hashObject(context).then((hash) => {
        setState((prev) => ({ ...prev, contextHash: hash.slice(0, 8) }));
      });
    }, []);

    // Memoize the message object to prevent recreation
    const message: IMessage = useMemo(
      () => ({
        isOpen: state.isOpen,
        wasOpen: state.wasOpen,
        open,
        close,
        setContext,
      }),
      [state.isOpen, state.wasOpen, open, close, setContext]
    );

    // Effect to update wasOpen status when navigation changes
    useEffect(() => {
      if (!hasLoaded) return;

      const wasOpen = !!navigation.dismissedMessages.find(
        (m) => m.contextHash === state.contextHash && m.name === name
      );

      setState((prev) => {
        if (prev.wasOpen !== wasOpen) {
          return { ...prev, wasOpen };
        }
        return prev;
      });
    }, [hasLoaded, navigation.dismissedMessages, state.contextHash, name]);

    // Effect to evaluate the condition and enqueue the message if the condition is met
    useEffect(() => {
      if (!hasLoaded) {
        enqueuedRef.current = false;
        return;
      }

      const conditionMet = condition({ user, tasks, navigation, userValues, whitelabelConfig, whitelabelLoading });
      const wasOpen = !!navigation.dismissedMessages.find(
        (m) => m.contextHash === state.contextHash && m.name === name
      );

      const shouldEnqueue =
        conditionMet &&
        state.contextHash && // the context needs to be set for the message to be enqueued
        (!wasOpen || recurring);

      if (shouldEnqueue && !enqueuedRef.current) {
        enqueueMessage({ name: name, message, order });
        enqueuedRef.current = true;
      } else if (!shouldEnqueue && enqueuedRef.current) {
        enqueuedRef.current = false;
      }
    }, [
      user,
      tasks,
      navigation,
      userValues,
      whitelabelConfig,
      whitelabelLoading,
      hasLoaded,
      state.contextHash,
      recurring,
      name,
      order,
      message,
      enqueueMessage,
    ]);

    // Effect to open message when it's first in queue
    useEffect(() => {
      if (messageQueue.length > 0 && messageQueue[0].name === name) {
        open();
      }
    }, [messageQueue, name, open]);

    // Return the message object
    return message;
  };
}
